import { alpha, Box, ButtonBase, Paper, Popper, type SxProps, type Theme } from '@mui/material';
import { useRef, useState, type ReactNode } from 'react';
import { mergeSx } from '../../../../utils/styles';

const styles = {
  button: (isActive: boolean) => (theme: Theme) => ({
    width: 40,
    height: 40,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: isActive ? 'secondary.450' : 'neutral.600',
    color: theme.palette.text.primary,
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
    ...(!isActive && {
      '&:hover': {
        backgroundColor: 'neutral.500',
      },
    }),
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  }),
  flyout: {
    minWidth: 184,
    maxHeight: 'min(70vh, 520px)',
    overflowY: 'auto',
    borderRadius: 3,
    backgroundColor: 'neutral.600',
    boxShadow: (theme: Theme) => `0 16px 32px ${alpha(theme.palette.common.black, 0.34)}`,
  },
} as const;

type NeobetCompactSidebarButtonProps = {
  'aria-label': string;
  children: ReactNode;
  flyoutContent?: ReactNode;
  flyoutTestId?: string;
  isActive?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  testId?: string;
};

const NeobetCompactSidebarButton = ({
  'aria-label': ariaLabel,
  children,
  flyoutContent,
  flyoutTestId,
  isActive = false,
  onClick,
  sx,
  testId,
}: NeobetCompactSidebarButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFlyoutOpen = Boolean(anchorEl && flyoutContent);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFlyout = (element: HTMLElement) => {
    clearCloseTimer();
    setAnchorEl(element);
  };

  const scheduleCloseFlyout = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setAnchorEl(null), 120);
  };

  return (
    <>
      <Box
        onMouseEnter={(event) => openFlyout(event.currentTarget)}
        onMouseLeave={scheduleCloseFlyout}
        onFocus={(event) => openFlyout(event.currentTarget)}
        onBlur={scheduleCloseFlyout}
        data-testid={testId}
      >
        <ButtonBase
          sx={mergeSx(styles.button(isActive || isFlyoutOpen), sx)}
          onClick={onClick}
          aria-label={ariaLabel}
          aria-haspopup={flyoutContent ? 'true' : undefined}
          aria-expanded={flyoutContent ? isFlyoutOpen : undefined}
        >
          {children}
        </ButtonBase>
      </Box>

      {flyoutContent ? (
        <Popper
          open={isFlyoutOpen}
          anchorEl={anchorEl}
          placement='right-start'
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          sx={{ zIndex: (theme) => theme.zIndex.appBar + 2 }}
        >
          <Paper
            elevation={0}
            sx={styles.flyout}
            data-testid={flyoutTestId}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleCloseFlyout}
            onFocus={clearCloseTimer}
            onBlur={scheduleCloseFlyout}
          >
            {flyoutContent}
          </Paper>
        </Popper>
      ) : null}
    </>
  );
};

export default NeobetCompactSidebarButton;
